import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Plus, Pencil, Trash2, Copy, QrCode, Link as LinkIcon,
  ExternalLink, Settings, Upload, Download, AlertTriangle, LogOut, Users, CreditCard, UserPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cardsApi, adminApi, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CardEditor from "@/components/CardEditor";
import QRDialog from "@/components/QRDialog";
import ImportDialog from "@/components/ImportDialog";
import SettingsDialog from "@/components/SettingsDialog";
import ClientEditor from "@/components/ClientEditor";

export default function Admin() {
  const nav = useNavigate();
  const { user, logout } = useAuth();

  const [cards, setCards] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterClientId, setFilterClientId] = useState("all");

  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [qrCard, setQrCard] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [clientEditing, setClientEditing] = useState(null);
  const [clientEditorOpen, setClientEditorOpen] = useState(false);
  const [deleteClientId, setDeleteClientId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, cl] = await Promise.all([cardsApi.list(), adminApi.listClients()]);
      setCards(c);
      setClients(cl);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clientById = useMemo(() => {
    const m = {};
    for (const c of clients) m[c.user_id] = c;
    return m;
  }, [clients]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cards.filter((c) => {
      if (filterClientId !== "all") {
        if (filterClientId === "unassigned" && c.client_id) return false;
        if (filterClientId !== "unassigned" && c.client_id !== filterClientId) return false;
      }
      if (!q) return true;
      return [c.full_name, c.email, c.slug, c.company, c.job_title]
        .filter(Boolean).some((f) => f.toLowerCase().includes(q));
    });
  }, [cards, search, filterClientId]);

  const openCreate = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (card) => { setEditing(card); setEditorOpen(true); };

  const onSaved = async () => { setEditorOpen(false); await load(); toast.success("Card saved"); };
  const doDelete = async () => {
    if (!deleteId) return;
    try {
      await cardsApi.remove(deleteId);
      toast.success("Card deleted");
      setDeleteId(null);
      await load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };
  const doDuplicate = async (id) => {
    try { await cardsApi.duplicate(id); toast.success("Duplicated"); await load(); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  };
  const toggleActive = async (card) => {
    try { await cardsApi.update(card.id, { active: !card.active }); await load(); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  };
  const assignClient = async (cardId, clientId) => {
    try {
      await cardsApi.assign(cardId, clientId === "none" ? null : clientId);
      toast.success("Card reassigned");
      await load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const publicUrl = (slug) => `${window.location.origin}/card/${slug}`;
  const copyLink = async (slug) => {
    await navigator.clipboard.writeText(publicUrl(slug));
    toast.success("Public link copied");
  };

  const doLogout = async () => { await logout(); nav("/admin-portal/login"); };

  const doDeleteClient = async () => {
    if (!deleteClientId) return;
    try {
      await adminApi.deleteClient(deleteClientId);
      toast.success("Client deleted");
      setDeleteClientId(null);
      await load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const total = cards.length;
  const active = cards.filter((c) => c.active).length;

  return (
    <div className="min-h-screen bg-[#07111D] text-white">
      <header className="h-16 border-b border-[#1E2D40] flex items-center justify-between px-6 md:px-8 bg-[#07111D]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#18BFFF] flex items-center justify-center">
            <span className="text-[#07111D] font-bold text-sm">ED</span>
          </div>
          <div>
            <h1 className="font-display text-lg font-semibold leading-tight" data-testid="admin-title">Admin Panel</h1>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A73] leading-tight">{user?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="settings-btn" onClick={() => setSettingsOpen(true)}
                  className="border-[#1E2D40] bg-transparent text-white hover:bg-[#162436] hover:text-white">
            <Settings className="w-4 h-4 mr-2" />Settings
          </Button>
          <Button variant="outline" size="sm" onClick={doLogout} data-testid="logout-btn"
                  className="border-[#1E2D40] bg-transparent text-white hover:bg-[#162436] hover:text-white">
            <LogOut className="w-4 h-4 mr-2" />Sign out
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-8 py-8">
        <Tabs defaultValue="cards">
          <TabsList className="bg-[#0B131E] border border-[#1E2D40]">
            <TabsTrigger value="cards" data-testid="admin-tab-cards" className="gap-2"><CreditCard className="w-3.5 h-3.5" />Cards</TabsTrigger>
            <TabsTrigger value="clients" data-testid="admin-tab-clients" className="gap-2"><Users className="w-3.5 h-3.5" />Clients</TabsTrigger>
          </TabsList>

          <TabsContent value="cards" className="mt-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard label="Total cards" value={total} testid="stat-total" />
              <StatCard label="Active" value={active} testid="stat-active" />
              <StatCard label="Draft" value={total - active} testid="stat-draft" />
            </div>

            <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-3 flex-1 max-w-2xl">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A73]" />
                  <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)}
                         data-testid="search-input"
                         className="pl-9 bg-[#0B131E] border-[#1E2D40] text-white placeholder:text-[#4A5A73] focus-visible:ring-[#18BFFF] focus-visible:border-[#18BFFF]" />
                </div>
                <Select value={filterClientId} onValueChange={setFilterClientId}>
                  <SelectTrigger data-testid="filter-client" className="w-full sm:w-56 bg-[#0B131E] border-[#1E2D40] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#101B28] border-[#1E2D40] text-white">
                    <SelectItem value="all">All clients</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {clients.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name || c.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-btn"
                        className="border-[#1E2D40] bg-transparent text-white hover:bg-[#162436] hover:text-white">
                  <Upload className="w-4 h-4 mr-2" />Import
                </Button>
                <a href={cardsApi.exportCsvUrl()} data-testid="export-btn"
                   className="inline-flex items-center rounded-md border border-[#1E2D40] bg-transparent px-3 py-2 text-sm font-medium text-white hover:bg-[#162436] transition-colors">
                  <Download className="w-4 h-4 mr-2" />Export CSV
                </a>
                <Button onClick={openCreate} data-testid="create-card-btn"
                        className="bg-[#18BFFF] text-[#07111D] hover:bg-[#4DD0FF] shadow-[0_0_15px_rgba(24,191,255,0.15)]">
                  <Plus className="w-4 h-4 mr-2" />New card
                </Button>
              </div>
            </div>

            <div className="rounded-xl border border-[#1E2D40] overflow-hidden bg-[#101B28]">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#1E2D40] hover:bg-transparent">
                    <TableHead className="text-[#94A3B8] font-medium">Name</TableHead>
                    <TableHead className="text-[#94A3B8] font-medium hidden lg:table-cell">Slug</TableHead>
                    <TableHead className="text-[#94A3B8] font-medium">Client</TableHead>
                    <TableHead className="text-[#94A3B8] font-medium">Status</TableHead>
                    <TableHead className="text-right text-[#94A3B8] font-medium">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && <TableRow className="border-[#1E2D40]"><TableCell colSpan={5} className="text-center py-8 text-[#94A3B8]">Loading...</TableCell></TableRow>}
                  {!loading && filtered.length === 0 && (
                    <TableRow className="border-[#1E2D40]"><TableCell colSpan={5} className="text-center py-8 text-[#94A3B8]" data-testid="empty-state">No cards found.</TableCell></TableRow>
                  )}
                  {filtered.map((c) => (
                    <TableRow key={c.id} className="border-[#1E2D40] hover:bg-[#162436]" data-testid={`row-${c.slug}`}>
                      <TableCell className="font-medium text-white">
                        {c.full_name || <span className="text-[#4A5A73]">Unnamed</span>}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <code className="text-xs bg-[#0B131E] border border-[#1E2D40] rounded px-2 py-0.5 text-[#18BFFF]">{c.slug}</code>
                      </TableCell>
                      <TableCell>
                        <Select value={c.client_id || "none"} onValueChange={(v) => assignClient(c.id, v)}>
                          <SelectTrigger data-testid={`assign-${c.slug}`} className="h-8 w-44 bg-[#0B131E] border-[#1E2D40] text-white text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#101B28] border-[#1E2D40] text-white">
                            <SelectItem value="none">— Unassigned —</SelectItem>
                            {clients.map((cl) => <SelectItem key={cl.user_id} value={cl.user_id}>{cl.name || cl.email}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch checked={c.active} onCheckedChange={() => toggleActive(c)} data-testid={`toggle-${c.slug}`}
                                  className="data-[state=checked]:bg-[#18BFFF]" />
                          <Badge className={c.active
                            ? "bg-[#18BFFF]/15 text-[#18BFFF] border border-[#18BFFF]/30 hover:bg-[#18BFFF]/15"
                            : "bg-[#162436] text-[#94A3B8] border border-[#1E2D40] hover:bg-[#162436]"}>
                            {c.active ? "Active" : "Draft"}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <IconBtn title="QR" onClick={() => setQrCard(c)} testid={`qr-${c.slug}`}><QrCode className="w-4 h-4" /></IconBtn>
                          <IconBtn title="Copy" onClick={() => copyLink(c.slug)} testid={`copy-${c.slug}`}><LinkIcon className="w-4 h-4" /></IconBtn>
                          <IconBtn title="Open" onClick={() => window.open(publicUrl(c.slug), "_blank")} testid={`open-${c.slug}`}><ExternalLink className="w-4 h-4" /></IconBtn>
                          <IconBtn title="Edit" onClick={() => openEdit(c)} testid={`edit-${c.slug}`}><Pencil className="w-4 h-4" /></IconBtn>
                          <IconBtn title="Duplicate" onClick={() => doDuplicate(c.id)} testid={`dup-${c.slug}`}><Copy className="w-4 h-4" /></IconBtn>
                          <IconBtn title="Delete" onClick={() => setDeleteId(c.id)} testid={`del-${c.slug}`} danger><Trash2 className="w-4 h-4" /></IconBtn>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-[#4A5A73]" data-testid="count-label">Showing {filtered.length} of {total} cards</p>
          </TabsContent>

          <TabsContent value="clients" className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#94A3B8]">Create, edit, and remove client accounts. Assign cards from the Cards tab.</p>
              <Button onClick={() => { setClientEditing(null); setClientEditorOpen(true); }} data-testid="create-client-btn"
                      className="bg-[#18BFFF] text-[#07111D] hover:bg-[#4DD0FF]">
                <UserPlus className="w-4 h-4 mr-2" />New client
              </Button>
            </div>
            <div className="rounded-xl border border-[#1E2D40] overflow-hidden bg-[#101B28]">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#1E2D40] hover:bg-transparent">
                    <TableHead className="text-[#94A3B8] font-medium">Client</TableHead>
                    <TableHead className="text-[#94A3B8] font-medium">Email</TableHead>
                    <TableHead className="text-[#94A3B8] font-medium">Cards</TableHead>
                    <TableHead className="text-right text-[#94A3B8] font-medium">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.length === 0 && (
                    <TableRow className="border-[#1E2D40]"><TableCell colSpan={4} className="text-center py-8 text-[#94A3B8]">No clients yet.</TableCell></TableRow>
                  )}
                  {clients.map((cl) => (
                    <TableRow key={cl.user_id} className="border-[#1E2D40] hover:bg-[#162436]" data-testid={`client-row-${cl.user_id}`}>
                      <TableCell className="font-medium text-white flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#0B131E] border border-[#1E2D40] flex items-center justify-center overflow-hidden">
                          {cl.company_logo_url ? <img src={cl.company_logo_url} alt="" className="w-full h-full object-cover" /> :
                            <span className="text-[10px] text-[#18BFFF] font-bold">{(cl.name || cl.email).slice(0,2).toUpperCase()}</span>}
                        </div>
                        {cl.name || <span className="text-[#4A5A73]">—</span>}
                      </TableCell>
                      <TableCell className="text-[#94A3B8]">{cl.email}</TableCell>
                      <TableCell>
                        <Badge className="bg-[#162436] text-[#18BFFF] border border-[#1E2D40]">{cl.card_count}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <IconBtn title="Edit" onClick={() => { setClientEditing(cl); setClientEditorOpen(true); }} testid={`edit-client-${cl.user_id}`}><Pencil className="w-4 h-4" /></IconBtn>
                          <IconBtn title="Delete" onClick={() => setDeleteClientId(cl.user_id)} testid={`del-client-${cl.user_id}`} danger><Trash2 className="w-4 h-4" /></IconBtn>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {editorOpen && <CardEditor open={editorOpen} onOpenChange={setEditorOpen} card={editing} onSaved={onSaved} />}
      {qrCard && <QRDialog card={qrCard} open={!!qrCard} onOpenChange={(v) => !v && setQrCard(null)} />}
      {importOpen && <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} clients={clients} />}
      {settingsOpen && <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />}
      {clientEditorOpen && (
        <ClientEditor open={clientEditorOpen} onOpenChange={setClientEditorOpen} client={clientEditing} onSaved={async () => { setClientEditorOpen(false); await load(); toast.success("Client saved"); }} />
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="bg-[#101B28] border-[#1E2D40] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete this card?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#94A3B8]">This permanently removes the card and its public link.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#1E2D40] text-white hover:bg-[#162436]" data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[#FF453A] text-white hover:bg-[#FF6B60]" data-testid="delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteClientId} onOpenChange={(v) => !v && setDeleteClientId(null)}>
        <AlertDialogContent className="bg-[#101B28] border-[#1E2D40] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete this client?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#94A3B8]">All their assigned cards will become unassigned. Cards are not deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#1E2D40] text-white hover:bg-[#162436]" data-testid="delete-client-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteClient} className="bg-[#FF453A] text-white hover:bg-[#FF6B60]" data-testid="delete-client-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-[#1E2D40] bg-[#101B28] p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A73]">{label}</div>
      <div className="mt-1 font-display text-3xl font-semibold text-white">{value}</div>
    </div>
  );
}

function IconBtn({ children, onClick, title, testid, danger }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
        danger ? "text-[#94A3B8] hover:bg-[#FF453A]/15 hover:text-[#FF453A]" : "text-[#94A3B8] hover:bg-[#162436] hover:text-[#18BFFF]"
      }`}
    >{children}</button>
  );
}
